import {useTranslation} from 'react-i18next';
import {useFormikContext} from 'formik';
import {useSelector} from 'react-redux';
import {useRef, useState, useMemo} from 'react';
import {Button} from '@tickchak/react-share/app';
import {symbolDefDark, add} from '../../../../../../../assets/symbols/symbols/Symbols.css';
import {PriceGroup} from '../../../../../../../redux/subscription/subscription.type';
import PricingGroupPop from '../../../../pricingGroupPop/PricingGroupPop';
import useShow from '../../../../../../../hooks/useShow';
import List from '../../../../../../../component/list/List';
import NumberInput from '../../../../../../../component/input/NumberInput';
import * as css from '../../PricingStep/PricingStep.css';
import {SubscriptionFormValues} from '../../../createSubscriptionForm.types';
import PriceGroupButtonWithMenu from './PriceGroupButtonWithMenu';
import {usePriceGroups} from '../../PricingStep/hooks/usePriceGroups';
function PriceGroupsSection() {
    const priceGroupPopShow = useShow();
    const {priceGroups, handleSavePriceGroup} = usePriceGroups(priceGroupPopShow);
    const {t} = useTranslation('subscriptions');
    const {values, setFieldValue} = useFormikContext<SubscriptionFormValues>();
    const currencySymbol = useSelector((state: any) => state.event.currencySymbol);
    const priceGroupsListShow = useShow();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [editingPriceGroupId, setEditingPriceGroupId] = useState<number | null>(null);
    const refList = useRef(null);

    const priceGroupIds = values.priceGroupIds || [];
    const selectedPriceGroups = (priceGroups || []).filter((pg: PriceGroup) => priceGroupIds.includes(pg.spgId)
    );

    const handleRemovePriceGroup = (priceGroupId: number) => {
        const updatedIds = priceGroupIds.filter((id: number) => id !== priceGroupId);
        setFieldValue('priceGroupIds', updatedIds);
    };


    const handleEditPriceGroup = (e: React.MouseEvent, priceGroup: PriceGroup) => {
        e.stopPropagation();
        setEditingPriceGroupId(priceGroup.spgId);
        priceGroupPopShow.open();
    };

    const handleTogglePriceGroup = (priceGroupId: number) => {
        if(priceGroupIds.includes(priceGroupId)) {
            const updatedIds = priceGroupIds.filter((id: number) => id !== priceGroupId);
            setFieldValue('priceGroupIds', updatedIds);
        } else {
            const updatedIds = [...priceGroupIds, priceGroupId];
            setFieldValue('priceGroupIds', updatedIds);
        }
    };

    // Prepare list items for List component
    const priceGroupsListItems = useMemo(() =>  priceGroups?.map((priceGroup: PriceGroup) => {
            const isSelected = priceGroupIds.includes(priceGroup.spgId);
            return {
                key: priceGroup.spgId,
                text: `${priceGroup.title} (${priceGroup.minPrice}-${priceGroup.maxPrice}₪)`,
                type: 'checkbox',
                selected: isSelected,
                onClick: (item: any, selected: boolean) => {
                    if(item && item.key) {
                        handleTogglePriceGroup(item.key);
                    }
                }
            };
        }), [priceGroups, priceGroupIds, handleTogglePriceGroup]);

    const handleTogglePriceGroupsList = (e: React.MouseEvent) => {
        e.stopPropagation();
        if(priceGroupsListShow.isOpen) {
            priceGroupsListShow.close();
        } else {
            priceGroupsListShow.open();
        }
    };

    return (
        <css.sectionWrapper>
            <css.headerTitle $icon={symbolDefDark}>
                {t('redemptionSettings')}
            </css.headerTitle>
            <css.section>
                {/*<css.questionIcon>?</css.questionIcon>*/}
                <css.inputsRow>
                    <css.inputWrapper>
                        <css.localInputWrapper data-currency={currencySymbol}>
                            <NumberInput
                                name="redemptionPrice"
                                text={t('price')}
                                icon={null}
                                className=""
                                forwardRef={null}
                                events={{
                                    onClick: priceGroupPopShow.open
                                }}
                            />
                        </css.localInputWrapper>
                    </css.inputWrapper>
                    <css.inputWrapper>
                        <css.localInputWrapper data-currency={currencySymbol}>
                            <NumberInput
                                name="redemptionUpToPrice"
                                text={t('upToPrice')}
                                icon={null}
                                className=""
                                forwardRef={null}
                                events={{
                                    onClick: priceGroupPopShow.open
                                }}
                            />
                        </css.localInputWrapper>
                    </css.inputWrapper>
                    <Button variant="brand" size="category" onClick={priceGroupPopShow.open} >{t('add')}</Button>
                </css.inputsRow>
                <css.priceGroups>
                    {selectedPriceGroups.map((priceGroup: PriceGroup) => (
                        <PriceGroupButtonWithMenu
                            key={priceGroup.spgId}
                            priceGroup={priceGroup}
                            onEdit={handleEditPriceGroup}
                            onDelete={() => handleRemovePriceGroup(priceGroup.spgId)}
                            t={t}
                        />
                    ))}
                    <css.addPriceGroup ref={dropdownRef}>
                        <Button iconStart={<img src={add} width={16} height={16} alt="" />} variant="brand" size="category" onClick={handleTogglePriceGroupsList} />
                        <css.addPriceGroupText onClick={handleTogglePriceGroupsList} ref={refList} >{t('changePriceGroup')}</css.addPriceGroupText>
                        {priceGroupsListShow.isOpen && (
                            <List
                                list={priceGroupsListItems.length > 0 ? priceGroupsListItems : [{key: 'empty', text: t('noAvailablePriceGroups'), disabled: true}]}
                                showClass={priceGroupsListShow.showClass}
                                close={priceGroupsListShow.close}
                                top={27}
                                fixedTop={refList.current.getBoundingClientRect().y}
                                width="300px"
                                left={refList.current?.getBoundingClientRect().x-40 + 'px'} 

                            />
                        )}
                    </css.addPriceGroup>
                </css.priceGroups>
                <css.descriptionText>{t('priceGroupDescription')}</css.descriptionText>
            </css.section>

            {priceGroupPopShow.isOpen && (
                <PricingGroupPop
                    show={priceGroupPopShow}
                    onSave={async (values) => {
                        await handleSavePriceGroup(values);
                        setEditingPriceGroupId(null);
                    }}
                    priceGroupId={editingPriceGroupId}
                    priceGroups={priceGroups}
                />
            )}
        </css.sectionWrapper>
    );
}

export default PriceGroupsSection;

